import { useState, useEffect } from 'react';
import moment from 'moment';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import Comment from '@IssueRepresentationalDisplay/Comment';
import LabelClickUpMenu from '@CommonRepresentationalDisplay/LabelClickUpMenu';
import '@Partials/_ComStyles.scss';
import './History.scss';

export default function History(props) {

    const [historyUser] = useState({});
    const [oldAssigneeUser] = useState({fullName: 'Unassigned', userName:'unassigned', id: 0})
    const [newAssigneeUser] = useState({fullName: 'Unassigned', userName:'unassigned', id: 0})
    const [history, setHistory] = useState({});
    const colorList = ['#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0', '#9a60b0', '#6cb060',
            '#b0ae60', '#65412b', '#cc9963', '#e1bd83', '#60748b', '#744700', '#c8d579', '#5f8fba', '#3e6073', '#8e6dc1', '#473785',
           '#9158a9',  '#7f3f7a', '#583e84', '#073763', '#20124d', '#3d124d', '#64113f', '#780448', '#003e23', '#108764', '#1a4c3d',
            '#c8d77f', '#11888b', '#1a95bd', '#0d6c7b', '#25b56e', '#006e3d'];
    const className = 'history__agile';

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async() => {
        let historyObj;
        if(props.userInteraction.linkType === 'history'){
            historyObj = props.historyList.find((history) => history.id === props.userInteraction.linkId);
            setHistory(historyObj);
        }
    }

    const getInitials = (string) => {
        if(!!string && string.length > 0) {
            var names = string?.split(' '),
                initials = names[0]?.substring(0, 1)?.toUpperCase();

            if (names?.length > 1) {
                initials += names[names?.length - 1]?.substring(0, 1)?.toUpperCase();
            }
            return initials;
        }
        return 'N/A';
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        return moment(timestamp).format('MMM D, YYYY h:mm A');
    };

    const splitUserNames = (user) => {
        if (!user) return 'Unknown';
        return user.fullName || user.userName || 'Unknown';
    };

    const renderAvatar = (user, size = 32) => {
        if (!user || !user.id) return null;
        
        const backgroundColor = colorList[user.id % colorList.length];
        const initials = getInitials(user.fullName || user.userName || 'U');

        return (
            <Avatar
                sx={{
                    width: size,
                    height: size,
                    bgcolor: backgroundColor,
                    fontSize: size === 32 ? '0.875rem' : '0.75rem',
                    fontWeight: 600,
                    border: `2px solid ${alpha(backgroundColor, 0.3)}`
                }}
                alt={user.fullName}
            >
                {initials}
            </Avatar>
        );
    };

    const renderStatusChip = (statusValue) => {
        const statusLabels = {
            'inprogress': 'In Progress'
        };

        // Find the actual status from adminFieldList if statusValue is an ID
        let statusString = statusValue;
        let statusColor = '#2784B9'; // default color
        
        if (props.adminFieldList && typeof statusValue === 'number') {
            const statusField = props.adminFieldList.find((field) => field.title === 'status');
            if (statusField) {
                try {
                    const options = JSON.parse(statusField.options);
                    const statusOption = options.find((option) => option.id === statusValue);
                    if (statusOption) {
                        statusString = statusOption.title;
                        statusColor = statusOption.color || '#2784B9';
                    } else {
                        statusString = String(statusValue);
                    }
                } catch (e) {
                    statusString = String(statusValue);
                }
            }
        } else {
            statusString = String(statusValue || '');
        }

        const statusLower = statusString ? String(statusString).toLowerCase() : '';
        const label = statusLabels[statusLower] || (statusString ? statusString.charAt(0).toUpperCase() + statusString.slice(1) : 'Unknown');

        return (
            <Chip
                label={label}
                size="small"
                sx={{
                    bgcolor: alpha(statusColor, 0.12),
                    color: statusColor,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 24,
                    textTransform: 'capitalize',
                }}
                variant="outlined"
            />
        );
    };

    return (
        <Box className={className}>
            {props.userInteraction.historyType?.toLowerCase() !== 'clientcomment' && (
                <Box className={className + '__card'}>
                    {/* Create Event */}
                    {history?.property?.toLowerCase() === 'create' && (
                        <Box className={className + '__create'}>
                            <Divider className={className + '__divider'}>
                                <Chip
                                    label={
                                        <Box className={className + '__createLabel'}>
                                            <span>Issue created</span>
                                            <span className={className + '__time'}>{formatTime(history?.timeStamp)}</span>
                                        </Box>
                                    }
                                    size="small"
                                    className={className + '__createChip'}
                                />
                            </Divider>
                        </Box>
                    )}

                    {/* Status Update */}
                    {history?.property?.toLowerCase() === 'statusupdate' && (
                        <Box>
                            <Box className={className + '__cardHeader'}>
                                {renderAvatar(historyUser)}
                                <Box className={className + '__cardHeaderText'}>
                                    <Typography className={className + '__userName'}>{splitUserNames(historyUser)}</Typography>
                                    <Typography className={className + '__time'}>{formatTime(history?.timeStamp)}</Typography>
                                </Box>
                            </Box>
                            <Box className={className + '__cardBody'}>
                                <Typography className={className + '__action'}>Status Updated</Typography>
                                <Box className={className + '__changeRow'}>
                                    {renderStatusChip(history?.details?.oldStatus)}
                                    <Typography className={className + '__arrow'}>→</Typography>
                                    {renderStatusChip(history?.details?.newStatus)}
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Assignee Update */}
                    {history?.property?.toLowerCase() === 'assigneeupdate' && (
                        <Box>
                            <Box className={className + '__cardHeader'}>
                                {renderAvatar(historyUser)}
                                <Box className={className + '__cardHeaderText'}>
                                    <Typography className={className + '__userName'}>{splitUserNames(historyUser)}</Typography>
                                    <Typography className={className + '__time'}>{formatTime(history?.timeStamp)}</Typography>
                                </Box>
                            </Box>
                            <Box className={className + '__cardBody'}>
                                <Typography className={className + '__action'}>Assignee Updated</Typography>
                                <Box className={className + '__changeRow'}>
                                    <Box className={className + '__userChip'}>
                                        {renderAvatar(oldAssigneeUser, 24)}
                                        <Typography className={className + '__userChipName'}>
                                            {splitUserNames(oldAssigneeUser)}
                                        </Typography>
                                    </Box>
                                    <Typography className={className + '__arrow'}>→</Typography>
                                    <Box className={className + '__userChip'}>
                                        {renderAvatar(newAssigneeUser, 24)}
                                        <Typography className={className + '__userChipName'}>
                                            {splitUserNames(newAssigneeUser)}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Label Update */}
                    {history?.property === 'Label' && (
                        <Box>
                            <Box className={className + '__cardHeader'}>
                                {renderAvatar(historyUser)}
                                <Box className={className + '__cardHeaderText'}>
                                    <Typography className={className + '__userName'}>{splitUserNames(historyUser)}</Typography>
                                    <Typography className={className + '__time'}>{formatTime(history?.timeStamp)}</Typography>
                                </Box>
                            </Box>
                            <Box className={className + '__cardBody'}>
                                <Typography className={className + '__action'}>
                                    {history?.action === 'add' ? 'Label Added' : 'Label Removed'}
                                </Typography>
                                <Box className={className + '__changeRow'}>
                                    <LabelClickUpMenu history={history?.registryLinkObj} />
                                </Box>
                            </Box>
                        </Box>
                    )}

                    {/* Comments */}
                    {(props.userInteraction.linkType === 'comment' || props.userInteraction.linkType === 'clientComment') && (
                        <Comment
                            userInteraction={props.userInteraction}
                            ticket={props?.ticket}
                            commentList={props.commentList}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
}
